import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Menu, X, Phone, Mail } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Top bar con info de contacto */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4 py-2">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center gap-4">
              <a href="tel:+525219841387001" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
                <Phone size={14} />
                <span className="hidden sm:inline">{t('header.phone')}</span>
              </a>
              <a href="mailto:ejecutivo@fd-inmobiliaria.homes" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
                <Mail size={14} />
                <span className="hidden md:inline">{t('header.email')}</span>
              </a>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-amber-400 font-semibold">
                {t('header.certified')}
              </div>
              <LanguageSelector />
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="bg-gradient-to-br from-blue-900 to-blue-700 p-2 rounded-lg shadow-lg group-hover:shadow-xl transition-shadow">
              <Building2 className="text-amber-400" size={32} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-blue-900 font-heading">FD-Inmobiliaria</h1>
              <p className="text-xs text-amber-600 font-semibold font-heading">Real Estate Services</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`font-medium transition-colors ${
                isActive('/') 
                  ? 'text-blue-900 border-b-2 border-amber-500' 
                  : 'text-gray-600 hover:text-blue-900'
              }`}
            >
              {t('nav.properties')}
            </Link>
            <Link
              to="/acerca"
              className={`font-medium transition-colors ${
                isActive('/acerca') 
                  ? 'text-blue-900 border-b-2 border-amber-500' 
                  : 'text-gray-600 hover:text-blue-900'
              }`}
            >
              {t('nav.about')}
            </Link>
            <Link
              to="/admin"
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-2 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-md hover:shadow-lg font-medium"
            >
              {t('nav.admin')}
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-blue-900 hover:text-amber-600 transition-colors"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={`font-medium py-2 px-4 rounded transition-colors ${
                  isActive('/') 
                    ? 'bg-blue-900 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('nav.properties')}
              </Link>
              <Link
                to="/acerca"
                onClick={() => setIsMenuOpen(false)}
                className={`font-medium py-2 px-4 rounded transition-colors ${
                  isActive('/acerca') 
                    ? 'bg-blue-900 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t('nav.about')}
              </Link>
              <Link
                to="/admin"
                onClick={() => setIsMenuOpen(false)}
                className="bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2 px-4 rounded-lg text-center font-medium"
              >
                {t('nav.admin')}
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
